import { html, LitElement } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { DebugService } from '../services/debug.service.js';
import { style } from './carousel.style.js';

class RefiCarousel extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiCarousel');
    // pagination is 1-based
    this.selected = 2;
    this.touchStartX = 0;
    this.touchMoveX = 0;
    this.swipeThreshold = 30;
    this.itemsLoaded = false;
    this.handleItemsLoaded = this.handleItemsLoaded.bind(this);
    this.handleCarouselResize = this.handleCarouselResize.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.handleCarouselResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.handleCarouselResize);
    window.removeEventListener('load', this.handleItemsLoaded);
  }

  firstUpdated() {
    this.itemsLoaded = true;
    this.numElements = this.determineNumberOfElements();
    this.selected = Math.round(this.numElements / 2);
    this.scrollToCurrentIndex();
  }

  static get properties() {
    return {
      numElements: { type: Number, attribute: false },
      selected: { type: Number, attribute: 'selected', reflect: true },
    };
  }

  static get styles() {
    return [style];
  }

  determineNumberOfElements() {
    const slot = this.shadowRoot.querySelector('slot');
    const children = slot.assignedElements({ flatten: true });
    return children.length;
  }

  handlePageChange(event) {
    this.selected = event.detail.newValue;
    this.debug.log('page changed to', this.selected);
    this.scrollToCurrentIndex();
    this.dispatchEvent(
      new CustomEvent('carouselPageChanged', {
        detail: {
          newValue: this.selected,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  showPrevious() {
    const prevSelected = this.selected || 0;
    this.selected = Math.max(this.selected - 1, 1);
    if (prevSelected !== this.selected) {
      this.handlePageChange({ detail: { newValue: this.selected } });
    }
    this.scrollToCurrentIndex();
  }

  showNext() {
    const prevSelected = this.selected || 0;
    const slot = this.shadowRoot.querySelector('slot');
    const children = slot.assignedElements({ flatten: true });
    this.selected = Math.min(this.selected + 1, children.length);
    if (prevSelected !== this.selected) {
      this.handlePageChange({ detail: { newValue: this.selected } });
    }
    this.scrollToCurrentIndex();
  }

  handleTouchStart(event) {
    this.touchStartX = event.changedTouches[0].clientX;
  }

  handleTouchMove(event) {
    this.touchMoveX = event.changedTouches[0].clientX;
  }

  handleTouchEnd() {
    let deltaX = this.touchMoveX - this.touchStartX;
    if (this.touchMoveX === 0) {
      const carousel = this.shadowRoot.querySelector('.carousel-container');
      deltaX = carousel.clientWidth / 2 - this.touchStartX;
    }
    if (deltaX > this.swipeThreshold) {
      this.showPrevious();
    } else if (deltaX < -this.swipeThreshold) {
      this.showNext();
    }
  }

  scrollToCurrentIndex() {
    if (!this.itemsLoaded) return;
    // ensure scrolling happens after the resize window event so offsetWidth is set
    requestAnimationFrame(() => {
      const carousel = this.shadowRoot.querySelector('.carousel-content-container');
      const slot = this.shadowRoot.querySelector('slot');
      const children = slot.assignedElements({ flatten: true });
      const itemWidth = children[0].offsetWidth;

      carousel.scrollLeft = (this.selected - 1) * itemWidth - itemWidth / 2;
    });
  }

  handleCarouselResize() {
    this.debug.log('Window resized');
    this.scrollToCurrentIndex();
  }

  handleItemsLoaded() {
    this.itemsLoaded = true;
    this.scrollToCurrentIndex();
  }

  render() {
    return html` <div class="carousel-container" style="${styleMap({ marginBottom: this.numElements === 1 ? '16px' : '' })}">
      <div class="carousel-content-container" @touchstart="${this.handleTouchStart}" @touchmove="${this.handleTouchMove}" @touchend="${this.handleTouchEnd}" style="${styleMap({ justifyContent: this.numElements === 1 ? 'center' : '' })}">
        <slot></slot>
      </div>
      <slot name="additional-content"></slot>
      ${this.numElements > 1
        ? html`<div class="tab-indicator">
            <refi-arrow-pagination
              page-count="${this.numElements}"
              current-page="${this.selected}"
              accessibility-label="Offer select pagination"
              compact="false"
              navigation-type="in-page"
              @selectedUpdated="${this.handlePageChange}"
            ></refi-arrow-pagination>
          </div>`
        : ''}
    </div>`;
  }
}

customElements.define('refi-carousel', RefiCarousel);
